import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import {
  getOtpExpirationMinutes,
  LOGIN_MFA_EXPIRATION_MINUTES,
} from "@profile/components/organisms/MFACodeContainer/mfa-code-container.constants";
import type { OtpFlow } from "@profile/components/organisms/MFACodeContainer/mfa-code-container.types";

function computeRemainingSeconds(expiresAtMs: number): number {
  return Math.max(0, Math.floor((expiresAtMs - Date.now()) / 1000));
}

function toTimerParts(totalSeconds: number): { minutes: number; seconds: number } {
  return {
    minutes: Math.floor(totalSeconds / 60),
    seconds: totalSeconds % 60,
  };
}

function toTotalSeconds(minutes: number): number {
  return Math.max(0, Math.floor(minutes)) * 60;
}

function resolveStaticExpirationMinutes(
  flow: OtpFlow,
  factorType?: "email" | "sms",
): number {
  if (flow === "login") {
    return LOGIN_MFA_EXPIRATION_MINUTES;
  }
  return getOtpExpirationMinutes(factorType);
}

type UseOtpExpiryTimerOptions = {
  flow?: OtpFlow;
  factorType?: "email" | "sms";
  stateTokenExpiresAt?: number;
  onExpire?: () => void;
  onTimerExpired?: () => void;
};

export function useOtpExpiryTimer({
  flow = "registration",
  factorType,
  stateTokenExpiresAt,
  onExpire,
  onTimerExpired,
}: UseOtpExpiryTimerOptions) {
  const isLoginFlow = flow === "login";
  const isRecoveryFlow = flow === "recovery";
  const usesWallClockExpiry =
    (isLoginFlow || isRecoveryFlow) && stateTokenExpiresAt != null;
  const usesStateTokenExpiry = isLoginFlow && stateTokenExpiresAt != null;

  const initialExpirationMinutes = resolveStaticExpirationMinutes(flow, factorType);
  const initialRemainingSeconds = toTotalSeconds(initialExpirationMinutes);

  const [remainingSeconds, setRemainingSeconds] = useState(initialRemainingSeconds);
  const [isExpired, setIsExpired] = useState(false);
  const [showExpiryModal, setShowExpiryModal] = useState(false);
  const [tick, setTick] = useState(0);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasMarkedExpiredRef = useRef(initialRemainingSeconds === 0);
  const onExpireRef = useRef(onExpire);
  const onTimerExpiredRef = useRef(onTimerExpired);

  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  useEffect(() => {
    onTimerExpiredRef.current = onTimerExpired;
  }, [onTimerExpired]);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const markExpired = useCallback(() => {
    if (hasMarkedExpiredRef.current) {
      return;
    }
    hasMarkedExpiredRef.current = true;
    flushSync(() => {
      setIsExpired(true);
    });
    onTimerExpiredRef.current?.();
    if (isLoginFlow) {
      setShowExpiryModal(true);
    }
    onExpireRef.current?.();
  }, [isLoginFlow]);

  const resetExpiryTracking = useCallback(() => {
    hasMarkedExpiredRef.current = false;
    setIsExpired(false);
    setShowExpiryModal(false);
  }, []);

  const startStaticTimer = useCallback(() => {
    clearTimer();
    timerRef.current = setInterval(() => {
      setRemainingSeconds((prevSeconds) => {
        if (prevSeconds <= 1) {
          clearTimer();
          return 0;
        }
        return prevSeconds - 1;
      });
    }, 1000);
  }, [clearTimer]);

  const timerDisplay = useMemo(() => {
    if (usesWallClockExpiry && stateTokenExpiresAt != null) {
      return toTimerParts(computeRemainingSeconds(stateTokenExpiresAt));
    }
    return toTimerParts(remainingSeconds);
  }, [usesWallClockExpiry, stateTokenExpiresAt, remainingSeconds, tick]);

  // Elapsed-time tick for login and recovery OTP deadlines
  useEffect(() => {
    if (!usesWallClockExpiry || stateTokenExpiresAt == null) {
      return;
    }

    const syncFromWallClock = () => {
      setTick((currentTick) => currentTick + 1);
    };

    const timer = setInterval(syncFromWallClock, 1000);
    document.addEventListener("visibilitychange", syncFromWallClock);
    window.addEventListener("focus", syncFromWallClock);

    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", syncFromWallClock);
      window.removeEventListener("focus", syncFromWallClock);
    };
  }, [stateTokenExpiresAt, usesWallClockExpiry]);

  useEffect(() => {
    if (!usesWallClockExpiry || stateTokenExpiresAt == null) {
      return;
    }

    resetExpiryTracking();
  }, [resetExpiryTracking, stateTokenExpiresAt, usesWallClockExpiry]);

  useEffect(() => {
    if (!usesWallClockExpiry || stateTokenExpiresAt == null) {
      return;
    }

    const remaining = computeRemainingSeconds(stateTokenExpiresAt);
    if (remaining !== 0) {
      return;
    }

    queueMicrotask(() => {
      markExpired();
    });
  }, [markExpired, stateTokenExpiresAt, usesWallClockExpiry, tick]);

  // Static countdown for registration and flows without a persisted deadline
  useEffect(() => {
    if (usesWallClockExpiry) {
      return;
    }

    if (initialRemainingSeconds > 0) {
      startStaticTimer();
    }

    return () => {
      clearTimer();
    };
  }, [clearTimer, initialRemainingSeconds, startStaticTimer, usesWallClockExpiry]);

  useEffect(() => {
    if (usesWallClockExpiry) {
      return;
    }

    if (remainingSeconds === 0 && !hasMarkedExpiredRef.current) {
      markExpired();
    }
  }, [markExpired, remainingSeconds, usesWallClockExpiry]);

  const resetTimer = useCallback(
    (minutesOverride?: number) => {
      const nextMinutes =
        minutesOverride ?? resolveStaticExpirationMinutes(flow, factorType);
      const nextRemaining = toTotalSeconds(nextMinutes);

      clearTimer();
      hasMarkedExpiredRef.current = nextRemaining === 0;
      setRemainingSeconds(nextRemaining);
      resetExpiryTracking();

      if (nextRemaining > 0 && !usesWallClockExpiry) {
        startStaticTimer();
      }
    },
    [
      clearTimer,
      factorType,
      flow,
      resetExpiryTracking,
      startStaticTimer,
      usesWallClockExpiry,
    ],
  );

  const dismissExpiryModal = useCallback(() => {
    setShowExpiryModal(false);
  }, []);

  return {
    minutes: timerDisplay.minutes,
    seconds: timerDisplay.seconds,
    isExpired,
    showExpiryModal,
    usesStateTokenExpiry,
    usesWallClockExpiry,
    resetTimer,
    resetExpiryTracking,
    markExpired,
    dismissExpiryModal,
  };
}
